"use client";

import React from "react";
import { ArrowUpRight, CircleCheck as CheckCircle2, Zap, Sparkles, Lightbulb, ChartBar as BarChart3, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
const pillars = [{
  id: "energy-procurement",
  badge: "Procurement & Tariffs",
  title: "Energy Procurement",
  icon: Zap,
  inquiry: "Optimising your electricity & gas contracts?",
  description: "Electricity and gas procurement, contract renewals, fixed and tracker options, tariff reviews, supplier comparisons, contract-end-date management, MIC and consumption reviews.",
  deliverables: ["Electricity and gas procurement", "Contract renewals & tariff reviews", "Fixed and tracker options", "Supplier comparisons & end-date tracking", "MIC and consumption reviews"],
  ctaText: "Request an Energy Review",
  href: "/FreeEnergyReviewPage"
}, {
  id: "energy-audits-grants",
  badge: "Audits & Grants",
  title: "Energy Audits, Grants & Supports",
  icon: Sparkles,
  inquiry: "Accessing grant funding & commercial audits?",
  description: "Commercial energy audits, SEAI audit support, identifying relevant grants and supports, helping businesses understand available funding options, and coordinating appropriately qualified energy auditors.",
  deliverables: ["Commercial energy audits", "SEAI audit support & voucher guidance", "Identifying relevant grants & supports", "Funding options clarification", "Coordinating qualified energy auditors"],
  ctaText: "Check SEAI Eligibility",
  href: "/SeaiEnergyAuditAndEligibilityPage"
}, {
  id: "energy-projects",
  badge: "Efficiency & Upgrades",
  title: "Energy Projects",
  icon: Lightbulb,
  inquiry: "Planning high-impact facility improvements?",
  description: "Coordination for Solar PV, heat pumps, HVAC, heating controls, BMS and controls, LED lighting, refrigeration, insulation and building fabric improvements, EV charging, and other energy-efficiency projects.",
  deliverables: ["Solar PV, heat pumps & HVAC", "Heating controls & BMS integration", "LED lighting & commercial refrigeration", "Insulation & building fabric improvements", "EV charging & efficiency projects"],
  ctaText: "View Energy Projects",
  href: "/ServicesPage"
}, {
  id: "energy-management",
  badge: "Data & Monitoring",
  title: "Energy Management",
  icon: BarChart3,
  inquiry: "Tracking interval data & baseline consumption?",
  description: "Ongoing energy reviews, 15-minute interval-data analysis, baseload identification, consumption monitoring, contract management, project follow-up, and ongoing energy-cost management.",
  deliverables: ["Ongoing energy reviews", "15-minute interval-data analysis", "Baseload identification & leak checks", "Continuous consumption monitoring", "Contract management & project follow-up"],
  ctaText: "Request an Energy Review",
  href: "/FreeEnergyReviewPage"
}];
const processSteps = [{
  step: "01",
  name: "Review"
}, {
  step: "02",
  name: "Procure"
}, {
  step: "03",
  name: "Audit"
}, {
  step: "04",
  name: "Plan"
}, {
  step: "05",
  name: "Fund"
}, {
  step: "06",
  name: "Deliver"
}, {
  step: "07",
  name: "Manage"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section data-section-id="77062" id="core-energy-services" data-nav="light" className="bg-background text-foreground py-12 md:py-16 lg:py-18 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center mb-8 md:mb-10">
          <div className="lg:col-span-7 space-y-4">
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 uppercase tracking-widest text-xs px-3 py-1 font-semibold">
              Commercial Energy Management
            </Badge>
            <h2 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              Energy Management &amp; Project Coordination.{" "}
              <span className="text-primary block sm:inline">
                Real Savings.
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-border aspect-square bg-muted">
              <video src="https://website-6d8a7bbf.bdd.bho.mybluehost.me/wp-content/uploads/2026/10/217514_medium.mp4" autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        <div className="relative w-full h-px bg-border mb-8 md:mb-10" aria-hidden="true">
          <div className="absolute left-1/3 -top-0.5 w-12 h-1 bg-primary rounded-sm" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} variants={cardVariants} className="flex">
                <Card className="flex flex-col justify-between w-full bg-card text-card-foreground border-border rounded-xl p-6 hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md">
                  <div>
                    <CardHeader className="p-0 space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 font-medium text-xs px-2.5 py-1">
                          {pillar.badge}
                        </Badge>
                        <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <IconComponent className="w-5 h-5" />
                        </div>
                      </div>

                      <div>
                        <h3 className="font-sans text-lg sm:text-xl font-semibold text-foreground tracking-tight leading-snug">
                          {pillar.title}
                        </h3>
                      </div>

                      <div className="bg-background border border-border rounded-md p-3.5 relative">
                        <span className="font-serif text-2xl leading-none text-muted-foreground/40 absolute top-2 left-2.5 select-none">
                          &ldquo;
                        </span>
                        <p className="font-serif italic text-sm text-foreground pl-3.5 leading-snug">
                          {pillar.inquiry}
                        </p>
                      </div>
                    </CardHeader>

                    <CardContent className="p-0 pt-5 space-y-5">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>

                      <div className="space-y-2.5 pt-2 border-t border-border/60">
                        <p className="text-xs font-semibold tracking-wider uppercase text-foreground">
                          Key Deliverables:
                        </p>
                        <ul className="space-y-2">
                          {pillar.deliverables.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                              <span className="leading-tight">{item}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="p-0 pt-6 mt-6 border-t border-border/60">
                    <Button asChild variant="default" className="w-full justify-between h-10 px-3.5 text-xs sm:text-sm font-semibold bg-primary text-primary-foreground hover:bg-[hsl(152,68%,28%)] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
                      <Link to={pillar.href} newTab={false} className="cursor-pointer">
                        <span>{pillar.ctaText}</span>
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* CMI Process Bar */}
        <div className="mt-16 md:mt-20 rounded-xl bg-card border border-border p-6 md:p-8 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-border/70">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                End-to-End Coordination
              </p>
              <h3 className="font-sans text-xl sm:text-2xl font-bold text-foreground mt-1">
                The CMI Process
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-xl">
              CMI coordinates appropriately qualified specialists from initial assessment to project delivery, providing clear oversight and accountability at every stage.
            </p>
          </div>

          <div className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {processSteps.map((step, idx) => <div key={step.step} data-index={idx} className="relative flex flex-col p-3 rounded-lg bg-background border border-border/80">
                  <span className="text-[11px] font-mono font-medium text-primary">
                    {step.step}
                  </span>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-sans font-semibold text-sm text-foreground">
                      {step.name}
                    </span>
                    {idx < processSteps.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60 hidden lg:block -mr-1" />}
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-8 bg-secondary text-secondary-foreground rounded-lg p-8 md:p-12 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="space-y-3 max-w-2xl">
            <h4 className="font-sans text-xl sm:text-2xl font-bold text-secondary-foreground">
              Spend less, Save more.
            </h4>
            <p className="text-sm text-secondary-foreground/90 font-sans leading-relaxed">
              CMI coordinates qualified specialist contractors and auditors across every stage of your energy strategy. There is no fee charged by CMI to the customer for our service.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground font-semibold hover:bg-[hsl(152,68%,28%)] hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
              <Link to="/FreeEnergyReviewPage">
                Request a Call Back
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}